import {Injectable} from '@angular/core';
import {Store} from '@ngrx/store';
import {
    AuthService,
    EventService,
    LoginEvent,
    ProductSearchConnector,
    ProductSearchService,
    StateWithProduct
} from '@spartacus/core';


import {AimoSearchConfig} from '../../../model/misc.model';

import {AimoCloseQuickSearchProducts, AimoQuickSearchProducts, AimoSearchProducts,} from './aimo-product-search.action';
import {Observable, of} from "rxjs";
import {AimoProduct} from "../../../model/product.model";
import {distinctUntilChanged, map, shareReplay, switchMap} from "rxjs/operators";
import {GTMItemListId} from "../../gtm/aimo-gtm.model";
import {
    AimoCMSCarouselComponentData,
    CAROUSEL_TYPE,
    SEARCH_CONFIG
} from "../../../cms-components/shared/carousel/cms/aimo-cms-carousel.component";
import {AimoProductService} from "../aimo-product.service";
import {ActivatedRoute} from "@angular/router";
import {AimoGtmService} from "../../gtm/aimo-gtm.service";
import {AimoActiveCartService} from "../../cart/aimo-active-cart.service";
import {AimoGtmStoredParams} from "../../../model/cart.model";
import {Actions, ofType} from "@ngrx/effects";
import {TOGGLE_FAVOURITE_PRODUCT} from "../../user/aimo-user.action";
import {AimoProductListComponentService} from "../aimo-product-list-component.service";

@Injectable({
    providedIn: 'root',
})
export class AimoProductSearchService extends ProductSearchService {
    constructor(private store2: Store<StateWithProduct>,
                protected productService: AimoProductService,
                protected productSearchConnector: ProductSearchConnector,
                protected gtmService: AimoGtmService,
                protected auth: AuthService,
                private actions$: Actions,
                protected eventService: EventService,
                protected cartService: AimoActiveCartService,) {
        super(store2);
        this.eventService
            .get(LoginEvent)
            .subscribe(() => this.productCache = {});
        this.actions$.pipe(
            ofType(TOGGLE_FAVOURITE_PRODUCT)
        ).subscribe(() => this.clearCache(CAROUSEL_TYPE.FAVORITES));

        // clear cache every 10 minutes
        setInterval(
            () => {
                this.productCache = {};
            }, 1000 * 60 * 10
        );
    }

    private clearCache(type: CAROUSEL_TYPE) {
        Object.values(this.productCache)
            .filter(cacheItem => cacheItem.carouselType === type)
            .forEach(cacheItem => delete this.productCache[cacheItem.key])
    }

    search(query: string, searchConfig?: AimoSearchConfig, auxiliary?: boolean): void {
        this.store.dispatch(
            searchConfig.suggestiveMode
                ? new AimoQuickSearchProducts(
                    {
                        queryText: query,
                        searchConfig,
                    },
                    searchConfig.origin,
                    auxiliary,
                )
                : new AimoSearchProducts(
                    {
                        queryText: query,
                        searchConfig,
                    },
                    searchConfig.origin,
                    auxiliary,
                    searchConfig.gtmParams
                ),
        );
    }

    closeQuickSearch(): void {
        this.store2.dispatch(new AimoCloseQuickSearchProducts());
    }

    productCache: { string?: CacheItem } = {};

    getProducts(component: AimoCMSCarouselComponentData, cartPage: boolean, gtmParams: AimoGtmStoredParams, activatedRoute: ActivatedRoute, ownAssortment: boolean): Observable<AimoProduct[]> {
        const cacheKey = this.createCacheKey(component, ownAssortment, activatedRoute);
        const cachedItem = this.productCache[cacheKey];
        if (cachedItem) {
            Object.assign(gtmParams, cachedItem.gtmParams);
            return cachedItem.payload;
        }
        const ret = this.getCartCode(cartPage)
            .pipe(
                distinctUntilChanged((curr, prev) => curr === prev),
                switchMap((cartCode) => {
                    if (gtmParams.gtmPromotionId == undefined) {
                        gtmParams.gtmPromotionId = component.uid;
                    }
                    if (gtmParams.gtmPromotionName == undefined) {
                        gtmParams.gtmPromotionName = component.name;
                    }
                    const sort = component.randomSort === 'true' ? 'random' : 'relevance';
                    const conf = {
                        ...SEARCH_CONFIG,
                        sort: sort,
                        cartId: cartCode,
                        disablePromoFields: true
                    } as AimoSearchConfig;
                    if (component.carouselType === CAROUSEL_TYPE.PRODUCT_LIST) {
                        if (gtmParams.gtmItemListId === undefined) {
                            gtmParams.gtmItemListId = cartPage ? GTMItemListId.frontPageCampaign : GTMItemListId.cartCampaign;
                        }
                        if (component.productCodes) {
                            return this.productService
                                .getProducts(component.productCodes.split(' '), cartCode)
                                .pipe(
                                    map((list) =>
                                        list?.sort(() => (component.randomSort === 'true' ? (Math.random() > 0.5 ? 1 : -1) : 1)),
                                    ),
                                );
                        } else if (component.categoryCodes) {
                            const param = component.categoryCodes
                                .split(' ')
                                .map((p) => ':allCategories:' + p)
                                .join('');
                            return this.productSearchConnector.search('*:' + sort + param, conf).pipe(map((res) => res.products));
                        }
                    } else {
                        if (component.carouselType === CAROUSEL_TYPE.LAST_PURCHASES) {
                            const query = this.getCarouselQuery(component, activatedRoute);
                            return this.searchCarouselProducts(sort, query, cartPage ? GTMItemListId.cartMostPurchased : GTMItemListId.frontPageMostPurchased, conf, gtmParams);
                        } else if (component.carouselType === CAROUSEL_TYPE.NOVELTY) {
                            const query = this.getCarouselQuery(component, activatedRoute, 'novelty');
                            return this.searchCarouselProducts(sort, 'novelty:true', cartPage ? GTMItemListId.cartNovelty : GTMItemListId.frontPageNovelty, conf, gtmParams);
                        } else if (component.carouselType === CAROUSEL_TYPE.CAMPAIGN) {
                            const query = this.getCarouselQuery(component, activatedRoute, 'campaign');
                            return this.searchCarouselProducts(sort, 'campaign:true', cartPage ? GTMItemListId.cartCampaign : GTMItemListId.frontPageCampaign, conf, gtmParams);
                        }
                        if (component.carouselType === CAROUSEL_TYPE.SPONSORED) {
                            const query = this.getCarouselQuery(component, activatedRoute, 'sponsored');
                            return this.searchCarouselProducts(sort, 'sponsored:true', cartPage ? GTMItemListId.cartSponsored : GTMItemListId.frontPageSponsored, conf, gtmParams);
                        }
                        if (component.carouselType === CAROUSEL_TYPE.SEASONAL) {
                            const query = this.getCarouselQuery(component, activatedRoute, 'seasonal');
                            return this.searchCarouselProducts(sort, 'seasonal:true', cartPage ? GTMItemListId.cartSeasonal : GTMItemListId.frontPageSeasonal, conf, gtmParams);
                        }
                        if (component.carouselType === CAROUSEL_TYPE.FAVORITES) {
                            const query = this.getCarouselQuery(component, activatedRoute, 'favorites');
                            return this.searchCarouselProducts(sort, 'favorites:true', cartPage ? GTMItemListId.cartFavorites : GTMItemListId.favorites, conf, gtmParams);
                        }
                    }
                    return of([]);
                }),
            ).pipe(
                shareReplay(1)
            );
        if (component.carouselType !== CAROUSEL_TYPE.LAST_PURCHASES) { // do not cache these
            this.productCache[cacheKey] = new CacheItem(cacheKey, component.carouselType, ret, gtmParams);
        }
        return ret;
    }

    private getCarouselQuery(component: AimoCMSCarouselComponentData, activatedRoute: ActivatedRoute, filterParam?: string): string {
        const isCategory = activatedRoute.snapshot.url.length > 1 ? (activatedRoute.snapshot.url[0]?.path === 'category' || activatedRoute.snapshot.url[0]?.path === 'c') : false;
        const category = isCategory ? activatedRoute.snapshot.url[1].path : undefined;
        const dummyCategory = AimoProductListComponentService.isDummyCategory(category);
        const categoryFilter = isCategory ? dummyCategory ? ':' + category + ':true' : ':allCategories:' + category : '';
        const mostPurchasedFilter = component.visibleOnlyFor ? component.visibleOnlyFor.split(' ').map(v => 'mostPurchasedIndustries:' + v).join(':') : filterParam ? '' : 'mostPurchased:true';
        const filter = filterParam ? ':' + filterParam + ':true' : '';
        return (mostPurchasedFilter + categoryFilter + filter);
    }

    getCartCode(cartPage: boolean): Observable<string> {
        return cartPage ? this.auth.isUserLoggedIn()
            .pipe(switchMap((isLoggedIn) => {
                if (isLoggedIn) {
                    return this.cartService.getActive().pipe(map(cart => cart?.code));
                }
                return of(undefined);
            })) : of(undefined);
    }

    private searchCarouselProducts(sort: string, query: string, gtmItemListId: GTMItemListId, conf: AimoSearchConfig, gtmParams: AimoGtmStoredParams) {
        if (gtmParams.gtmItemListId == undefined) {
            gtmParams.gtmItemListId = gtmItemListId;
        }
        return this.productSearchConnector
            .search('*:' + sort + ':' + query, conf)
            .pipe(map((res) => res.products));
    }

    private createCacheKey(component: AimoCMSCarouselComponentData, ownAssortment: boolean, activatedRoute: ActivatedRoute): string {
        return component.uid + '_' + (ownAssortment ? '_own' : '_all') + '_' + (activatedRoute.snapshot.url.length > 1 ? activatedRoute.snapshot.url[0]?.path : '');
    }
}

class CacheItem {
    constructor(public key, public carouselType: CAROUSEL_TYPE, public payload: Observable<AimoProduct[]>, public gtmParams: AimoGtmStoredParams) {
    }
}
